import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star, User, Calendar, MessageSquare, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackRiepilogo = ({ agencyId }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFeedbacks: 0,
    averageRating: 0,
    interestedCount: 0,
    notInterestedCount: 0
  });

  useEffect(() => {
    fetchFeedbacks();
  }, [agencyId]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/v1/matches/with-feedback`);
      
      // Filter only feedbacks for this agency where candidate submitted feedback
      const agencyFeedbacks = response.data.filter(
        match => match.agency_id === agencyId && match.candidate_feedback_submitted_at
      );
      
      setFeedbacks(agencyFeedbacks);
      
      // Calculate stats
      const total = agencyFeedbacks.length;
      const avgRating = total > 0 
        ? agencyFeedbacks.reduce((sum, f) => sum + (f.candidate_feedback_rating || 0), 0) / total 
        : 0;
      const interested = agencyFeedbacks.filter(f => f.candidate_interested_in_continuing).length;
      const notInterested = total - interested;
      
      setStats({
        totalFeedbacks: total,
        averageRating: avgRating.toFixed(1),
        interestedCount: interested,
        notInterestedCount: notInterested
      });
      
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('Errore nel caricamento feedback');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Totale Feedback</p>
                <p className="text-2xl font-bold text-blue-900">{stats.totalFeedbacks}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Media Rating</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-yellow-900">{stats.averageRating}</p>
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
              </div>
              <TrendingUp className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Interessati</p>
                <p className="text-2xl font-bold text-green-900">{stats.interestedCount}</p>
              </div>
              <div className="text-3xl">✅</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Non Interessati</p>
                <p className="text-2xl font-bold text-red-900">{stats.notInterestedCount}</p>
              </div>
              <div className="text-3xl">❌</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedbacks List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-600" />
            Feedback Ricevuti dai Candidati
          </CardTitle>
        </CardHeader>
        <CardContent>
          {feedbacks.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessun feedback ricevuto ancora</p>
              <p className="text-sm text-gray-400 mt-2">I feedback dei candidati appariranno qui dopo i colloqui</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedbacks.map((feedback) => (
                <div
                  key={feedback.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {feedback.candidate_name || 'Candidato'}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {new Date(feedback.candidate_feedback_submitted_at).toLocaleDateString('it-IT')}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {renderStars(feedback.candidate_feedback_rating)}
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          feedback.candidate_interested_in_continuing
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {feedback.candidate_interested_in_continuing ? '✅ Interessato' : '❌ Non interessato'}
                      </span>
                    </div>
                  </div>
                  
                  {feedback.candidate_feedback_comment && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-700 italic">
                        "{feedback.candidate_feedback_comment}"
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackRiepilogo;
